import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoChat } from "@/shared/model/edo-chat.model";
import { IUser } from "@/shared/model/user.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getParticipationsByUser } from "@/shared/reducers/entities/edo-chat-participation.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
} from "@/components/ui";
import axios from "axios";
import { SocketContext } from "context/socket";
import { useContext, useState, useCallback } from "react";
import { Translate } from "react-jhipster";
import { openChatWindow } from "@/shared/reducers/chat.reducer";

interface CreateChatProps {}

export const CreateChat = (_props: CreateChatProps) => {
  const dispatch = useAppDispatch();
  const { updating } = useAppSelector<EntityState<IEdoChat>>(
    (state) => state.edoChat
  );
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [utenteList, setUtenteList] = useState<IUser[]>([]);
  const [selectedUtente, setSelectedUtente] = useState<IUser | null>(null);
  const [utenteValue, setUtenteValue] = useState<string>("");
  const [loadingUtenteList, setLoadingUtenteList] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const socket = useContext(SocketContext);

  const handleCloseDialog = () => setOpen(false);

  const reloadPartecipazioneList = useCallback(() => {
    dispatch(
      getParticipationsByUser({
        page: 0,
        size: 1000,
        sort: "id:ASC",
      })
    );
  }, [dispatch]);

  const searchUtente = useCallback(
    async (query: string) => {
      setLoadingUtenteList(true);
      const roleFilter = hasAnyAuthority(account?.roles ?? [], [
        AUTHORITIES.ROLE_STUDENT,
      ])
        ? { "filter.roles.name": "$eq:" + AUTHORITIES.ROLE_TEACHER }
        : {};
      const response = await axios.get("/users", {
        params: {
          page: 0,
          size: 5,
          sortBy: "firstName:ASC",
          search: query,
          "filter.id": "$not:" + account?.id,
          ...roleFilter,
        },
      });
      setUtenteList(response.data.data ?? []);
      setLoadingUtenteList(false);
    },
    [account]
  );

  const createChat = async () => {
    if (!selectedUtente) return;
    const response = await axios.post("/chats/chat", {
      user: selectedUtente,
    });
    handleCloseDialog();
    reloadPartecipazioneList();
    setSelectedUtente(null);
    setUtenteValue("");
    const { chat, participation } = response.data;
    if (participation?.chat?.id != null) {
      dispatch(openChatWindow(participation.chat.id));
    }
    socket.emit("chat-created", chat);
  };

  const handleSelectUser = (user: IUser) => {
    setSelectedUtente(user);
    setUtenteValue("");
    setShowDropdown(false);
    setOpen(true);
  };

  return (
    <>
      <div className="relative">
        <Input
          label="Cerca altre persone"
          placeholder="Cerca altre persone"
          value={utenteValue}
          onChange={(e) => {
            const v = e.target.value;
            setUtenteValue(v);
            if (v.trim()) {
              setShowDropdown(true);
              searchUtente(v);
            } else {
              setShowDropdown(false);
            }
          }}
          onFocus={() => utenteValue.trim() && setShowDropdown(true)}
          onBlur={() =>
            setTimeout(() => setShowDropdown(false), 150)
          }
        />
        {showDropdown && (
          <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[var(--color-mockup-border)] bg-white py-1 shadow-lg dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]">
            {loadingUtenteList ? (
              <li className="px-3 py-2 text-sm text-[var(--color-mockup-text-light)]">Ricerca...</li>
            ) : utenteList.length === 0 ? (
              <li className="px-3 py-2 text-sm text-[var(--color-mockup-text-light)]">
                Nessun risultato
              </li>
            ) : (
              utenteList.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-primary/10"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectUser(user);
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inizia una nuova chat</DialogTitle>
          </DialogHeader>
          {selectedUtente && (
            <p className="text-body text-[var(--color-mockup-text-mid)]">
              Vuoi iniziare una nuova chat con{" "}
              {selectedUtente.firstName + " " + selectedUtente.lastName}?
            </p>
          )}
          <DialogFooter>
            <Button variant="outline-solid" onClick={handleCloseDialog}>
              <Translate contentKey="entity.action.cancel">Cancel</Translate>
            </Button>
            <Button onClick={createChat} disabled={updating}>
              Crea chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
